import { getTabId } from '@/lib/tabId';

// In dev: empty string → Vite proxy forwards /api/* to localhost:4000 (no CORS).
// In prod: VITE_API_URL is the absolute API origin (e.g. https://api.gatheredhq.com).
const API_BASE = (import.meta.env.VITE_API_URL as string | undefined) ?? '';

/**
 * Authenticated fetch against apps/api.
 *
 * Security model:
 *  - `credentials: 'include'` → browser auto-sends the HttpOnly `ghq_sid` cookie
 *  - `X-Tab-Id` header → server resolves the correct Redis session for this tab
 *  - No tokens are stored or sent by JS — the cookie is HttpOnly (XSS cannot read it)
 *
 * Session expiry:
 *  - On 401, redirects to login page (session expired or token refresh failed)
 *  - This happens automatically — no component-level handling needed
 */
/** A single Zod validation issue, as returned in `error.details`. */
export interface ApiErrorIssue {
  path?: (string | number)[];
  message?: string;
}

/** Error thrown by {@link apiFetch} for non-2xx responses. Carries the server's
 *  error code, HTTP status and (for validation errors) the field-level issues. */
export class ApiError extends Error {
  readonly code?: string;
  readonly status: number;
  readonly details?: ApiErrorIssue[];
  constructor(message: string, opts: { code?: string; status: number; details?: ApiErrorIssue[] }) {
    super(message);
    this.name = 'ApiError';
    this.code = opts.code;
    this.status = opts.status;
    this.details = opts.details;
  }
}

export async function apiFetch<T>(
  path: string,
  options: RequestInit = {},
): Promise<T> {
  const hasBody = options.body != null;

  const res = await fetch(`${API_BASE}${path}`, {
    ...options,
    credentials: 'include',   // send ghq_sid HttpOnly cookie automatically
    headers: {
      ...(hasBody ? { 'Content-Type': 'application/json' } : {}),
      'X-Tab-Id': getTabId(), // tab-isolated session lookup
      ...options.headers,
    },
  });

  if (!res.ok) {
    const body = await res.json().catch(() => ({})) as {
      error?: { code?: string; message?: string; details?: ApiErrorIssue[] };
    };

    // Session expired or invalid — force redirect to login
    if (res.status === 401 && !path.includes('/auth/')) {
      // Clear the presence cookie so marketing site shows Login instead of Dashboard
      document.cookie = 'ghq_logged_in=; path=/; max-age=0; SameSite=Lax';
      window.location.replace('/auth/login?error=session_expired');
      // Return a never-resolving promise so callers don't continue executing
      return new Promise<T>(() => {});
    }

    // Surface the first field-level validation issue (e.g. "Invalid website URL")
    // instead of the generic envelope message ("Request validation failed").
    const firstIssue = body.error?.details?.[0];
    const message = firstIssue?.message ?? body.error?.message ?? `API error ${res.status}`;
    throw new ApiError(message, {
      code: body.error?.code,
      status: res.status,
      details: body.error?.details,
    });
  }

  if (res.status === 204 || res.headers.get('content-length') === '0') {
    return undefined as T;
  }

  return res.json() as Promise<T>;
}

/** Handle for an in-flight upload — lets a caller cancel a queued/running file. */
export interface UploadHandle<T> {
  promise: Promise<T>;
  abort: () => void;
}

/**
 * Authenticated POST with real upload progress.
 *
 * Identical security model and error envelope to {@link apiFetch}, but built on
 * XMLHttpRequest because `fetch()` cannot report upload progress — there is no
 * spec'd way to observe request-body bytes with it.
 *
 * @param onProgress Reports 0–100 of REQUEST BODY BYTES delivered — nothing more.
 * Reaching 100 means the server now has the file and is working on it (sharp
 * resize, ffmpeg transcode); that work is invisible from here and can take far
 * longer than the upload itself, so callers must show a distinct "processing"
 * state between 100 and the promise settling.
 */
export function uploadWithProgress<T>(
  path: string,
  body: unknown,
  onProgress?: (percent: number) => void,
): UploadHandle<T> {
  const xhr = new XMLHttpRequest();

  const promise = new Promise<T>((resolve, reject) => {
    xhr.open('POST', `${API_BASE}${path}`, true);
    xhr.withCredentials = true; // ghq_sid HttpOnly cookie, same as apiFetch
    xhr.setRequestHeader('Content-Type', 'application/json');
    xhr.setRequestHeader('X-Tab-Id', getTabId());

    xhr.upload.onprogress = (e) => {
      if (!onProgress || !e.lengthComputable) return;
      onProgress(Math.round((e.loaded / e.total) * 100));
    };

    // Fires when the request BODY has been fully sent — i.e. the handover point to
    // the server. This is the honest "upload done, now processing" signal.
    // `xhr.onload` is NOT: it waits for the RESPONSE, which only arrives after the
    // server has finished transcoding. Reporting 100 there parked the bar at 99%
    // for the whole processing window and made the processing state unreachable.
    // Also covers servers that don't send Content-Length (lengthComputable false),
    // where onprogress never fires at all.
    xhr.upload.onload = () => onProgress?.(100);

    xhr.onload = () => {
      const parsed = (() => {
        try { return JSON.parse(xhr.responseText || '{}'); } catch { return {}; }
      })() as { error?: { code?: string; message?: string; details?: ApiErrorIssue[] } };

      if (xhr.status >= 200 && xhr.status < 300) {
        resolve(parsed as T);
        return;
      }

      // Mirror apiFetch: a dead session redirects rather than surfacing N per-file
      // errors. Never resolves, so callers stop executing.
      if (xhr.status === 401 && !path.includes('/auth/')) {
        document.cookie = 'ghq_logged_in=; path=/; max-age=0; SameSite=Lax';
        window.location.replace('/auth/login?error=session_expired');
        return;
      }

      const firstIssue = parsed.error?.details?.[0];
      reject(new ApiError(
        firstIssue?.message ?? parsed.error?.message ?? `API error ${xhr.status}`,
        { code: parsed.error?.code, status: xhr.status, details: parsed.error?.details },
      ));
    };

    xhr.onerror = () => reject(new ApiError('Network error during upload', { status: 0 }));
    xhr.ontimeout = () => reject(new ApiError('Upload timed out', { status: 0 }));
    xhr.onabort = () => reject(new ApiError('Upload cancelled', { code: 'UPLOAD_ABORTED', status: 0 }));

    xhr.send(JSON.stringify(body));
  });

  return { promise, abort: () => xhr.abort() };
}

/**
 * Share of the reported progress given to reading/encoding the file, the rest to
 * the network. The read is fast relative to a real upload (a phone on mobile data
 * spends nearly all its time on the wire), so it gets a small slice — but it isn't
 * free for a 30 MB video, and leaving it out pinned the bar at 0% while it ran.
 */
const ENCODE_SHARE = 0.1;

/** Read a File as a base64 data URL, reporting 0–100 of the read. */
function readAsBase64(file: File, onProgress?: (percent: number) => void): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onprogress = (e) => {
      if (onProgress && e.lengthComputable) onProgress(Math.round((e.loaded / e.total) * 100));
    };
    reader.onload = () => { onProgress?.(100); resolve(reader.result as string); };
    reader.onerror = () => reject(new ApiError('Could not read the file', { status: 0 }));
    reader.readAsDataURL(file);
  });
}

/**
 * Encode a File to base64 and POST it, reporting ONE continuous 0–100 across both
 * phases.
 *
 * Every upload surface previously inlined its own `fileToBase64` and started
 * reporting only once the request was already in flight, so the bar sat at 0
 * through the encode and then leapt. This keeps the number honest end-to-end:
 * it only ever describes work actually done, and 100 still means "the server has
 * the bytes", never "finished".
 *
 * Note the API takes base64 JSON, which inflates the payload ~4/3 — real bytes on
 * the wire are a third more than the file size. Sending raw binary would be
 * faster, but that's an endpoint change across every upload route.
 *
 * @param toBody Builds the request body from the encoded string (field names differ
 * per endpoint: `fileBase64`, `media`, `imageBase64`…).
 */
export async function uploadFileAsBase64<T>(
  path: string,
  file: File,
  toBody: (base64: string) => unknown,
  onProgress?: (percent: number) => void,
): Promise<T> {
  const base64 = await readAsBase64(
    file,
    onProgress ? (p) => onProgress(Math.round(p * ENCODE_SHARE)) : undefined,
  );

  return uploadWithProgress<T>(
    path,
    toBody(base64),
    onProgress
      ? (p) => onProgress(Math.round(ENCODE_SHARE * 100 + p * (1 - ENCODE_SHARE)))
      : undefined,
  ).promise;
}
